// encounter-list-rows.ts — the browser half of the encounter list tables (My Encounters, location.php's encounters).
//
// The endpoints (options/api/my_encounters_list_inc.php, location_encounters_list_inc.php) are a permission gate + ONE SELECT
// (ft_encounter_list_rows): they ship raw encounter rows under schema `encounter-list-v1`. This module turns them into the
// catalog table's {columns, rows} — every header, cell and the GM settings controls — so PHP compiles and renders nothing.
// Zone / environment / weather / light still fill through encounterDisplayCells() (render-table.ts) from `encounter_display`.

import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import type { EncounterDisplayInput } from '../janus-engine/port/k5-render/encounter-display';
import { resolveUrl } from './catalog-list';
import { appendIconAction, appendIconLink, catalogIcon } from './dom-helpers';
import type { CatalogTableResponse, CellSegment, TableCell, TableColumn, TableRow } from './types';
import type { EncounterListRow } from '../shared/encounter-list-row';

export type { EncounterListRow };

export const ENCOUNTER_LIST_SCHEMA = 'encounter-list-v1';

export interface EncounterListResponse {
    schema: typeof ENCOUNTER_LIST_SCHEMA;
    /** `manage` = the GM's My Encounters table (settings column); `location` = location.php's read-only table. */
    mode: 'manage' | 'location';
    /** Adventure scope with a family to move encounters to: prepend the move-selection checkbox column. */
    bulk: boolean;
    encounters: EncounterListRow[];
    /** Location ids this viewer may read; any other location / parent label renders blank. */
    readable_location_ids: string[];
    total: number;
    csrf_token?: string;
}

export function isEncounterListSchema(schema: unknown): schema is EncounterListResponse['schema'] {
    return schema === ENCOUNTER_LIST_SCHEMA;
}

const icon = (id: string, extra: Partial<Extract<CellSegment, { kind: 'icon' }>> = {}): CellSegment => ({ kind: 'icon', id, ...extra });
const empty = (): TableCell => ({ segments: [], sort: '', text: '' });

function column(key: string, header: CellSegment, label: string, sortable: boolean, sortType: 'text' | 'num', align: TableColumn['align'], width?: string): TableColumn {
    return { key, header_segments: [header], header_label: label, sortable, filterable: false, sort_type: sortType, align, ...(width ? { width } : {}) };
}

function encounterListColumns(mode: EncounterListResponse['mode'], bulk: boolean): TableColumn[] {
    const narrow = '25px';
    return [
        ...(bulk ? [column('move_select', icon('checkbox'), 'Select', false, 'text', 'center', '40px')] : []),
        ...(mode === 'manage' ? [column('settings', icon('encounter_settings'), 'Settings', false, 'text', 'center')] : []),
        column('location_parent', icon('location_parent'), 'Parent Location', true, 'text', 'left'),
        column('location', icon('location'), 'Location', true, 'text', 'left'),
        column('location_type', icon('location_type'), 'Location Type', false, 'text', 'center'),
        column('zone', icon('zone'), 'Zone', true, 'text', 'left'),
        column('encounter', icon('encounter'), 'Encounter', true, 'text', 'left'),
        column('rooms', icon('room', { title: 'Number of Rooms' }), 'Number of Rooms', true, 'num', 'center'),
        column('environment', icon('environment'), 'Environment', false, 'text', 'center', narrow),
        column('weather', icon('weather'), 'Weather', false, 'text', 'center', narrow),
        column('light', icon('light_type'), 'Light', false, 'text', 'center', narrow),
        column('music', icon('music'), 'Music', false, 'text', 'center', narrow),
        column('amb', icon('encounter_amb'), 'Ambience', false, 'text', 'center', narrow),
        column('amb_night', icon('encounter_amb_night'), 'Night Ambience', false, 'text', 'center', narrow),
        column('amb_weather', icon('encounter_amb_weather'), 'Weather Ambience', false, 'text', 'center', narrow),
    ];
}

function linkCell(name: string, href: string, target?: string): TableCell {
    return { segments: [{ kind: 'text', value: name, link: href, ...(target ? { target } : {}) }], sort: name, text: name.toLowerCase() };
}

function locationCell(id: string | null, name: string | null, readable: Set<string>): TableCell {
    const key = String(id ?? '').toUpperCase();
    const label = key && readable.has(key) ? decodeHtmlEntitiesForPlainText(name ?? '') : '';
    return label ? linkCell(label, resolveUrl(`location.php?location_id=${encodeURIComponent(key)}`), '_blank') : linkCell('', '');
}

function audioCell(id: string | null, iconId: string, variant: string): TableCell {
    return id ? { segments: [icon(iconId, { action: 'audio', actionArg: String(id), variant })], sort: '', text: '' } : empty();
}

/** Copy is a state change: a POST form carrying the encounter id + the session CSRF token, never a GET link. */
function appendCopyForm(parent: HTMLElement, encounterId: string, csrfToken: string): void {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = resolveUrl('options/tools/encounters/copy_encounter_inc.php');
    form.className = 'd-inline m-0';
    const fields: ReadonlyArray<readonly [string, string]> = [['encounter_id', encounterId], ['csrf_token', csrfToken]];
    for (const [name, value] of fields) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    }
    const button = document.createElement('button');
    button.type = 'submit';
    button.className = 'btn btn-sm btn-secondary';
    button.title = 'Copy encounter';
    button.setAttribute('aria-label', 'Copy encounter');
    button.innerHTML = catalogIcon('copy_encounter', 25, 25, 'Copy encounter');
    form.appendChild(button);
    parent.appendChild(form);
}

/** Edit / copy / private toggle / delete toggle — the GM cluster the old PHP printed with inline onclick strings. */
function settingsNode(row: EncounterListRow, csrfToken: string): Node {
    const id = row.encounter_id;
    const wrap = document.createElement('div');
    wrap.className = 'd-flex flex-column align-items-center gap-1';
    const line = (): HTMLDivElement => {
        const div = document.createElement('div');
        div.className = 'd-flex align-items-center justify-content-center gap-1';
        wrap.appendChild(div);
        return div;
    };
    const first = line();
    appendIconLink(first, resolveUrl(`encounter.php?scene=edit&encounter_id=${encodeURIComponent(id)}`), 'btn btn-sm btn-info', 'modify_encounter', 'Edit encounter');
    appendCopyForm(first, id, csrfToken);

    const second = line();
    const privacyUrl = resolveUrl('options/db_queries/update/update_privacy_inc.php');
    const setPrivate = async (value: '0' | '1'): Promise<void> => {
        await queryData(privacyUrl, 'div_sidelog_warning', { item_type: 'encounter', encounter_id: id, encounter_private: value });
        publicBtn.style.display = value === '1' ? 'none' : '';
        privateBtn.style.display = value === '1' ? '' : 'none';
    };
    const publicBtn = appendIconAction(second, 'btn btn-sm btn-primary', 'public', 'Make private', () => setPrivate('1'));
    const privateBtn = appendIconAction(second, 'btn btn-sm btn-danger', 'private', 'Make public', () => setPrivate('0'));
    const isPrivate = Number(row.encounter_private ?? 0) === 1;
    publicBtn.style.display = isPrivate ? 'none' : '';
    privateBtn.style.display = isPrivate ? '' : 'none';

    const del = appendIconAction(second, 'btn btn-sm btn-warning', 'delete', 'Delete encounter', () => {
        del.style.display = 'none';
        confirm.style.display = '';
    });
    const confirm = appendIconAction(second, 'btn btn-sm btn-danger', 'confirm', 'Confirm delete encounter', async () => {
        if (typeof openSideLog === 'function') openSideLog();
        // The 8th argument drops this catalog row on success (window.ftCatalogRemoveRow) — no page reload.
        await queryData(resolveUrl('options/db_queries/delete/soft_delete_inc.php'), 'div_sidelog_warning',
            { item_type: 'encounter', encounter_id: id, display_success_sidelog: '1' }, true, true, true, null, id);
    });
    confirm.style.display = 'none';
    return wrap;
}

function moveSelectNode(encounterId: string): Node {
    // Harvest contract of window.moveSelectedToAdventure: a checkbox whose id is the encounter UUID.
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = encounterId;
    input.name = encounterId;
    input.value = '1';
    input.className = 'form-check-input';
    input.style.transform = 'scale(1.6)';
    input.style.cursor = 'pointer';
    return input;
}

function encounterListRow(row: EncounterListRow, data: EncounterListResponse, readable: Set<string>): TableRow & { encounter_display: EncounterDisplayInput } {
    const id = row.encounter_id;
    const name = decodeHtmlEntitiesForPlainText(row.encounter_name ?? '');
    const rooms = Math.max(1, Number(row.encounter_room_count) || 1);
    const typeId = String(row.encounter_location_type ?? '');
    const typeName = decodeHtmlEntitiesForPlainText(row.encounter_location_type_name ?? '');
    const cells: Record<string, TableCell> = {
        location_parent: locationCell(row.encounter_location_parent_id, row.encounter_location_parent_name, readable),
        location: locationCell(row.encounter_location_id, row.encounter_location_name, readable),
        location_type: typeId ? { segments: [icon(typeId, { item_type: 'location_type', title: typeName })], sort: typeName, text: typeName.toLowerCase() } : empty(),
        encounter: linkCell(name, resolveUrl(`encounter.php?encounter_id=${encodeURIComponent(id)}`)),
        rooms: { segments: [{ kind: 'text', value: String(rooms) }], sort: rooms, text: String(rooms) },
        music: audioCell(row.encounter_mx, 'music', 'info'),
        amb: audioCell(row.encounter_amb, 'encounter_amb', 'warning'),
        amb_night: audioCell(row.encounter_amb_night, 'encounter_amb_night', 'light'),
        amb_weather: audioCell(row.encounter_amb_weather, 'encounter_amb_weather', 'light'),
    };
    if (data.mode === 'manage') cells.settings = { segments: [{ kind: 'node', build: () => settingsNode(row, data.csrf_token ?? '') }], sort: '', text: '' };
    if (data.bulk) cells.move_select = { segments: [{ kind: 'node', build: () => moveSelectNode(id) }], sort: '', text: '' };
    return { item_id: id, cells, encounter_display: { encounters: row as unknown as Record<string, unknown> } };
}

/** Raw `encounter-list-v1` rows → the catalog table model render-table.ts builds from. */
export function adoptEncounterList(data: EncounterListResponse): CatalogTableResponse {
    if (!isEncounterListSchema(data.schema) || !Array.isArray(data.encounters)) throw new Error('catalog-encounter-list-schema');
    const readable = new Set((data.readable_location_ids ?? []).map(id => String(id).toUpperCase()));
    return {
        columns: encounterListColumns(data.mode, data.bulk === true),
        rows: data.encounters.map(row => encounterListRow(row, data, readable)),
        total: data.encounters.length,
    };
}
